import { StartFreeButton } from '@/components/shared/StartFreeButton';
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from '@/components/ui/accordion';
import { Button } from '@/components/ui/button';
import { contentNavigationGroups } from '@/data/contentNavigation';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Sheet, SheetContent, SheetTrigger } from '@/components/ui/sheet';
import { supabase } from '@/integrations/supabase/client';
import { MARKETPLACE_CATEGORIES } from '@/lib/marketplaceNavigation';
import { cn } from '@/lib/utils';
import { useQuery } from '@tanstack/react-query';
import { BookOpen, Layout, Menu, Palette, Sparkles, Wand2 } from 'lucide-react';
import { useEffect, useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { contentNavigationIconMap } from './contentNavigationIcons';

const featureLinks = [
  { name: 'AI Funnel Suite™', hash: 'ai-funnel-suite', icon: Layout },
  { name: 'BookBuild™', hash: 'bookbuild', icon: BookOpen },
  { name: 'AI ExpertiseExtractor™', hash: 'ai-expertise-extractor', icon: Wand2 },
  { name: 'Premium Design Pack', hash: 'premium-design-pack', icon: Palette },
  { name: 'AI Writing Assistant Pro™', hash: 'ai-writing-assistant-pro', icon: Sparkles },
  { name: 'Publishing on Bookstera Marketplace', hash: 'bookstera-marketplace', icon: BookOpen },
  { name: 'Publishing to Amazon KDP', hash: 'amazon-kdp', icon: BookOpen },
  { name: 'How to Export Your Ebooks', hash: 'export-ebooks', icon: BookOpen },
];

interface MobileNavProps {
  user: any;
}

function LogoBranding({
  className = '',
  imgClassName = 'h-8',
  textClassName = 'ml-2 text-xl font-bold',
}: {
  className?: string;
  imgClassName?: string;
  textClassName?: string;
}) {
  return (
    <div className={`flex items-center ${className}`}>
      <img
        src="/lovable-uploads/bookstera-logo-nobg.webp"
        alt="Bookstera Logo"
        className={imgClassName}
        height="32"
      />

    </div>
  );
}

export function MobileNav({ user }: MobileNavProps) {
  const [isSheetOpen, setIsSheetOpen] = useState(false);
  const [activeMarketplaceCategory, setActiveMarketplaceCategory] =
    useState('All');
  const { pathname } = useLocation();
  const navigate = useNavigate();
  const isMarketplaceActive = pathname === '/marketplace' || pathname.startsWith('/marketplace/');

  interface TopBook {
    id: string;
    title: string;
    pseudonyms: { name: string } | null;
    book_cover_images: { image_url: string }[] | null;
  }

  const { data: topBooks, isLoading: isLoadingTopBooks } = useQuery<TopBook[]>({
    queryKey: ['mobile-top-books'],
    enabled: isSheetOpen,
    queryFn: async () => {
      const { data, error } = await supabase
        .from('books')
        .select('id, title, pseudonyms(name), book_cover_images(image_url)')
        .eq('marketplace_status', 'published')
        .order('created_at', { ascending: false })
        .limit(3);

      if (error) throw error;
      return data ?? [];
    },
  });

  const handleLinkClick = () => {
    setIsSheetOpen(false);
  };

  useEffect(() => {
    const handleCategoryChange = (event: CustomEvent) => {
      if (typeof event.detail?.category === 'string') {
        setActiveMarketplaceCategory(event.detail.category);
      }
    };

    window.addEventListener(
      'marketplace-category-changed',
      handleCategoryChange as EventListener
    );

    return () =>
      window.removeEventListener(
        'marketplace-category-changed',
        handleCategoryChange as EventListener
      );
  }, []);

  const handleMarketplaceCategoryClick = (category: string) => {
    setActiveMarketplaceCategory(category);
    setIsSheetOpen(false);

    const dispatchCategory = () => {
      window.dispatchEvent(
        new CustomEvent('marketplace-category-select', {
          detail: { category },
        })
      );
    };

    if (!isMarketplaceActive) {
      navigate('/marketplace');
      window.setTimeout(dispatchCategory, 0);
      return;
    }

    dispatchCategory();
  };

  return (
    <>
      <Sheet open={isSheetOpen} onOpenChange={setIsSheetOpen}>
        <SheetTrigger asChild>
          <Button
            aria-label="Open navigation"
            variant="ghost"
            size="icon"
            className="rounded-full border border-white/40 bg-white/70 text-slate-700 shadow-sm backdrop-blur hover:bg-secondary/10 hover:text-secondary"
          >
            <Menu aria-label="Open navigation" className="h-5 w-5" />
          </Button>
        </SheetTrigger>
        <SheetContent
          side="left"
          className="w-[85%] max-w-xs border-none bg-gradient-to-b from-white via-white/95 to-slate-100/90 px-0 pb-8 shadow-2xl"
        >
          <div className="flex h-full flex-col overflow-y-auto">
            <div className="px-6 pb-4 shadow-sm">
              <Link
                to="/"
                className="flex items-center justify-between"
                onClick={handleLinkClick}
              >
                <LogoBranding imgClassName="h-10" textClassName="ml-3 text-2xl font-semibold" />
              </Link>

            </div>

            <div className="flex-1 space-y-4 px-6 pt-6">
              <nav aria-label="Main navigation" className="space-y-1">
                {[
                  ['Create Books', '/create'],
                  ['Growth Autopilot', '/growth-autopilot'],
                  ['Marketplace', '/marketplace'],
                  ['Features', '/features'],
                  ['Pricing', '/pricing'],
                  ['Playbooks', '/self-publisher-playbooks'],
                  ['Partners', '/partners'],
                ].map(([name, href]) => (
                  <Link key={href} to={href} onClick={handleLinkClick} aria-current={pathname === href || pathname.startsWith(`${href}/`) ? 'page' : undefined} className={cn('flex items-center gap-1.5 rounded-xl px-4 py-3 text-sm font-semibold transition-colors', pathname === href || pathname.startsWith(`${href}/`) ? 'bg-secondary/10 text-secondary' : 'text-slate-700 hover:bg-slate-100')}>
                    {name}
                    {href === '/growth-autopilot' && (
                      <span className="rounded-full bg-secondary px-1.5 py-0.5 text-[9px] font-bold uppercase tracking-wide text-white">NEW</span>
                    )}
                  </Link>
                ))}
              </nav>

              {!isMarketplaceActive && (
                <>
                  <div className="space-y-1">
                    <Accordion
                      type="single"
                      collapsible
                      defaultValue="resources"
                      className="divide-y divide-slate-100"
                    >
                      <AccordionItem value="features">
                        <AccordionTrigger className="px-4 py-3 text-sm font-semibold text-slate-700 hover:text-secondary hover:no-underline">
                          Features
                        </AccordionTrigger>
                        <AccordionContent className="pb-2">
                          <div className="ml-2 space-y-0.5 border-l-2 border-secondary/20 pl-3">
                            {featureLinks.map((feature) => (
                              <Link
                                key={feature.hash}
                                to={`/features#${feature.hash}`}
                                onClick={handleLinkClick}
                                className="flex items-center gap-3 rounded-lg px-3 py-2.5 text-sm font-medium text-slate-600 transition hover:bg-secondary/5 hover:text-secondary"
                              >
                                <div className="flex h-6 w-6 items-center justify-center rounded-md bg-secondary/10 text-secondary">
                                  <feature.icon className="h-3.5 w-3.5" />
                                </div>
                                {feature.name}
                              </Link>
                            ))}
                          </div>
                        </AccordionContent>
                      </AccordionItem>
                      <AccordionItem value="resources">
                        <AccordionTrigger className="px-4 py-3 text-sm font-semibold text-slate-700 hover:text-secondary hover:no-underline">
                          Publishing Resources
                        </AccordionTrigger>
                        <AccordionContent className="pb-2">
                          <div className="ml-2 space-y-4 border-l-2 border-secondary/20 pl-3">
                            {contentNavigationGroups.map((group) => (
                              <section key={group.label} aria-label={group.label}>
                                <div className="px-3 pb-1 text-[10px] font-bold uppercase tracking-[0.16em] text-slate-400">{group.label}</div>
                                <div className="space-y-0.5">{group.items.map((item) => {
                                  const Icon = contentNavigationIconMap[item.icon];
                                  return (
                                    <Link key={item.href} to={item.href} onClick={handleLinkClick} className="flex items-center gap-3 rounded-lg px-3 py-2.5 text-sm font-medium text-slate-600 transition hover:bg-secondary/5 hover:text-secondary">
                                      <div className="flex h-6 w-6 shrink-0 items-center justify-center rounded-md bg-secondary/10 text-secondary">{item.emoji ? <span className="text-sm" aria-hidden="true">{item.emoji}</span> : <Icon className="h-3.5 w-3.5" />}</div>
                                      <span className="inline-flex items-center gap-2">
                                        {item.name}
                                        {item.badge && <span className="rounded-full bg-secondary px-1.5 py-0.5 text-[9px] font-bold uppercase tracking-wide text-white">{item.badge}</span>}
                                      </span>
                                    </Link>
                                  );
                                })}</div>
                              </section>
                            ))}
                          </div>
                        </AccordionContent>
                      </AccordionItem>
                    </Accordion>
                  </div>
                </>
              )}

              {isMarketplaceActive && (
                <div className="space-y-2">
                  <div className="px-4 text-xs font-semibold uppercase tracking-[0.18em] text-slate-400">
                    Marketplace
                  </div>
                  <Select
                    value={activeMarketplaceCategory}
                    onValueChange={handleMarketplaceCategoryClick}
                  >
                    <SelectTrigger className="h-11 rounded-xl border-orange-200 bg-white text-sm font-semibold text-slate-700 shadow-sm focus:ring-secondary/30">
                      <SelectValue placeholder="Category" />
                    </SelectTrigger>
                    <SelectContent className="max-h-80">
                      {MARKETPLACE_CATEGORIES.map((category) => (
                        <SelectItem key={category} value={category}>
                          {category}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              )}
            </div>

            <div className="border-t border-slate-100 px-6 pt-4 space-y-3 pb-6">
              {!user && (
                <Button
                  asChild
                  variant="outline"
                  className="w-full rounded-full border border-slate-200 bg-white py-2.5 text-sm font-semibold text-slate-700 shadow-sm hover:bg-slate-50"
                >
                  <Link to="/signin" onClick={handleLinkClick}>
                    Login
                  </Link>
                </Button>
              )}
              <StartFreeButton onClick={handleLinkClick} useHomepageAuthDestination />
            </div>
          </div>
        </SheetContent>
      </Sheet>
    </>
  );
}
